import { useCallback, useEffect, useState } from 'react';
import { loadCrmBoards } from './loadCrmBoards';

const isArchived = board => board.archived === true || (
  !board.isWebsiteBoard && !board.isStandaloneBoard && !board.isShared &&
  !board.active && !board.draft && board.matchingSubId?.status !== 'past_due'
);

export function useCRMQuests({ initialQuestId }) {
  const [quests, setQuests] = useState([]);
  const [archivedQuests, setArchivedQuests] = useState([]);
  const [selectedQuestId, setSelectedQuestId] = useState(initialQuestId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [availabilityWarning, setAvailabilityWarning] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refreshBoards = useCallback(() => setRefreshVersion(version => version + 1), []);

  useEffect(() => {
    let mounted = true;
    let pending = false;
    setLoading(true);

    const loadBoards = async () => {
      if (pending) return;
      pending = true;
      try {
        const result = await loadCrmBoards(initialQuestId);
        if (!mounted) return;
        if (!result || result.status >= 400 || !Array.isArray(result.data?.boards)) {
          setLoadError('Could not load CRM boards. Please try again.');
          return;
        }
        const boards = result.data.boards;
        const active = boards.filter(board => !isArchived(board));
        const ids = new Set(boards.map(board => String(board._id)));
        setQuests(previous => [...active, ...previous.filter(board => board.isStandaloneBoard && !ids.has(String(board._id)))]);
        setArchivedQuests(boards.filter(isArchived));
        setLoadError('');
        setAvailabilityWarning(result.data.warning || '');
      } catch {
        if (mounted) setLoadError('Could not load CRM boards. Please try again.');
      } finally {
        pending = false;
        if (mounted) setLoading(false);
      }
    };

    loadBoards();
    // Pull new submissions while the CRM is being used; never store a user's token.
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') loadBoards();
    }, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, [initialQuestId, refreshVersion]);

  return { quests, setQuests, archivedQuests, selectedQuestId, setSelectedQuestId,
    loading, loadError, availabilityWarning, refreshBoards };
}
